import type { DeepPartial } from 'ts-essentials';

import { iterateInlineTextNodes } from './iterateInlineTextNodes';
import type { AlineText, ParagraphElement } from '../../slate-api';
import { computePrimaryStyle } from '../helpers/computePrimaryStyle';
import type { PrimaryStyleRun } from '../helpers/computePrimaryStyle';
import type { CastPrimaryStyle, CastTextStyle } from '../types';

/**
 * Computes a paragraph's dominant typography from the DOCX source rather than
 * from the built CastText runs, because the run-level extractor deliberately
 * drops fontFamily (it would be noise per-run). The paragraph-level summary,
 * by contrast, is the one place fontFamily is useful — so we read it straight
 * from each AlineText's fontType here. We use `fontType.type` (the clean font
 * name, e.g. "Times New Roman") rather than `fontType.fontFamily` (a CSS font
 * stack), to match the clean names the PDF path produces.
 */
export function extractParagraphPrimaryStyle(
  paragraph: ParagraphElement
): CastPrimaryStyle | undefined {
  const runs: PrimaryStyleRun[] = [];
  for (const textNode of iterateInlineTextNodes(paragraph.children)) {
    runs.push(toRun(textNode));
  }
  return computePrimaryStyle(runs);
}

function toRun(node: DeepPartial<AlineText>): PrimaryStyleRun {
  const style: CastTextStyle = {};
  if (node.bold?.active) style.bold = true;
  if (node.italic?.active) style.italic = true;
  if (node.underline?.active) style.underline = true;
  if (node.fontSize?.size != null) style.fontSize = node.fontSize.size;
  if (node.fontType?.type != null) style.fontFamily = node.fontType.type;
  return {
    style: Object.keys(style).length > 0 ? style : undefined,
    text: node.text ?? '',
  };
}
